import React, { useRef, useEffect, useState } from "react";

type Props = {
    onComplete: () => void;
    width?: number;
    height?: number;
    children?: React.ReactNode; // Ce qu'on affiche "en dessous"
};

const CustomScratchCard: React.FC<Props> = ({
    onComplete,
    width = 250,
    height = 250,
    children,
}) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const isDrawing = useRef(false);
    const [complete, setComplete] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const image = new Image();
        image.src = "/dist/image/Gift-Defi.png";

        image.onload = () => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;

            const imgAspect = image.width / image.height;
            const canvasAspect = canvasWidth / canvasHeight;

            let drawWidth, drawHeight;

            if (imgAspect > canvasAspect) {
                drawWidth = canvasWidth;
                drawHeight = canvasWidth / imgAspect;
            } else {
                drawHeight = canvasHeight;
                drawWidth = canvasHeight * imgAspect;
            }

            const offsetX = (canvasWidth - drawWidth) / 2;
            const offsetY = (canvasHeight - drawHeight) / 2;

            // Dessiner l'image centrée
            ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);

            // Dessiner le texte "Gratter ici"
            ctx.font = "bold 28px Arial";
            ctx.fillStyle = "white";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.shadowColor = "black";
            ctx.shadowBlur = 10;
            ctx.fillText("Grattez ici", canvasWidth / 2, canvasHeight / 2);

            setImageLoaded(true);
        };

        image.onerror = () => {
            console.error("Erreur lors du chargement de l'image.");
        };
    }, [width, height]);

    useEffect(() => {
        const handleResize = () => {
            const screenWidth = window.innerWidth;
            const baseWidth = screenWidth < 500 ? screenWidth * 1.1 : width;
            const baseHeight = (baseWidth * height) / width;
            canvasRef.current!.width = baseWidth;
            canvasRef.current!.height = baseHeight;
        };

        handleResize(); // Initial call
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [width, height]);

    const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
        if (!imageLoaded) return;
        isDrawing.current = true;
        draw(e);
    };

    const handlePointerUp = () => {
        isDrawing.current = false;
        checkScratched();
    };

    const handlePointerMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (!isDrawing.current) return;
        draw(e);
    };

    const draw = (e: React.MouseEvent | React.TouchEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
        const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

        const x = clientX - rect.left;
        const y = clientY - rect.top;

        ctx.globalCompositeOperation = "destination-out";
        ctx.beginPath();
        ctx.arc(x, y, 20, 0, 2 * Math.PI);
        ctx.fill();
    };

    const checkScratched = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const imageData = ctx.getImageData(0, 0, width, height);
        const pixels = imageData.data;
        let transparentPixels = 0;

        for (let i = 3; i < pixels.length; i += 4) {
            if (pixels[i] === 0) transparentPixels++;
        }

        const percent = (transparentPixels / (width * height)) * 100;

        if (percent > 75 && !complete) {
            setComplete(true);
            onComplete();
        }
    };

    return (
        <div
            style={{
                position: "relative",
                width,
                height,
                borderRadius: "0.75rem",
                overflow: "hidden",
                justifyContent: "center", // Centrer horizontalement
                alignItems: "center", // Centrer verticalement
            }}
        >
            {/* Ce qu'on gratte pour révéler */}
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    zIndex: 1,
                    justifyContent: "center", // Centrer horizontalement
                    alignItems: "center", // Centrer verticalement
                    position: "absolute",
                    top: 0,
                    left: 0,
                }}
            >
                {children}
            </div>

            {/* Image à gratter par-dessus */}
            <canvas
                ref={canvasRef}
                width={width}
                height={height}
                className="scratch-card-canvas"
                style={{
                    width: "100%",
                    height: "auto",
                    display: "flex",
                    justifyContent: "center", // Centrer horizontalement
                    alignItems: "center", // Centrer verticalement
                    overflow: "hidden", // Empêche le canvas de déborder si l'image est trop grande
                    borderRadius: "0.75rem",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    zIndex: 2,
                }}
                onMouseDown={handlePointerDown}
                onMouseUp={handlePointerUp}
                onMouseMove={handlePointerMove}
                onTouchStart={handlePointerDown}
                onTouchEnd={handlePointerUp}
                onTouchMove={handlePointerMove}
            />
        </div>
    );
};

export default CustomScratchCard;
